/* ═══════════════════════════════════════════════════════════════════
   AdminRefreshDemoCard — one-click button that re-seeds the investor
   demo account so the Live Wave Dashboard always opens with fresh
   `sent_at` timestamps. Visible ONLY when the logged-in user has
   `role === 'admin'` (Lumina Wellness master admin).
   ═══════════════════════════════════════════════════════════════════ */
import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, RefreshCw, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminRefreshDemoCard() {
  const [state, setState] = useState('idle');   // idle | loading | success | error
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setState('loading'); setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/refresh-investor-demo`, {}, { withCredentials: true },
      );
      setLastResult(data.summary);
      setState('success');
      setTimeout(() => setState('idle'), 3600);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setState('error');
      setTimeout(() => setState('idle'), 4000);
    }
  };

  return (
    <div className="rounded-2xl p-4 flex items-center justify-between gap-3" data-testid="admin-refresh-demo-card"
         style={{ background: 'linear-gradient(135deg, rgba(255,107,189,0.08), rgba(0,212,255,0.04), #141820)',
                  border: '1px solid rgba(255,107,189,0.25)' }}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
             style={{ background: 'rgba(255,107,189,0.18)', border: '1px solid rgba(255,107,189,0.4)' }}>
          <Sparkles className="h-4 w-4" style={{ color: '#FF6BBD' }} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">Admin Tooling</p>
          <p className="text-sm font-black text-white truncate">Refresh Investor Demo</p>
          <p className="text-[11px] text-gray-400">Re-seeds Aurora Labs with 3 fresh live waves + paid invoices.</p>
        </div>
      </div>
      <button onClick={run} disabled={state === 'loading'}
              data-testid="refresh-investor-demo-btn"
              className="px-4 py-2.5 rounded-xl text-xs font-black flex items-center gap-2 whitespace-nowrap transition-all hover:brightness-110 disabled:opacity-60"
              style={
                state === 'success'
                  ? { background: '#4ade80', color: '#0a0a0a', border: '1px solid #4ade80' }
                  : { background: 'transparent', color: '#FF6BBD', border: '1px solid rgba(255,107,189,0.5)' }
              }>
        {state === 'loading' && <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Reseeding…</>}
        {state === 'success' && <><CheckCircle2 className="h-3.5 w-3.5" /> {lastResult?.waves_created || 3} waves fresh</>}
        {(state === 'idle' || state === 'error') && <><RefreshCw className="h-3.5 w-3.5" /> {state === 'error' ? 'Retry' : 'Refresh now'}</>}
      </button>
      {state === 'error' && (
        <p className="absolute mt-24 text-[10px] text-red-400" data-testid="refresh-error">{error}</p>
      )}
    </div>
  );
}
